// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, BookOpen, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GlossarioPage = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(null);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        let url = `${API_URL}/api/glossary`;
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (selectedLetter) params.append('letter', selectedLetter);
        if (params.toString()) url += `?${params.toString()}`;

        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setTerms(data);
        }
      } catch (error) {
        console.error('Error fetching glossary:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, [searchQuery, selectedLetter]);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const groupedTerms = terms.reduce((acc, term) => {
    const letter = term.term[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(term);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#1C1C1A] py-8" data-testid="glossario-page">
      <Helmet>
        <title>Glossario Finanziario (108+ termini) — SAPIENS</title>
        <meta name="description" content="Dizionario completo dei termini finanziari in italiano: ETF, dividendo, TER, inflazione, PAC, NAV e 100+ altri termini spiegati in modo semplice." />
        <link rel="canonical" href="https://finsapiens.it/glossario" />
      </Helmet>
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl font-medium text-[#E8E8E4] mb-4">Glossario finanziario</h1>
          <p className="text-lg text-[#888780] max-w-2xl">
            Tutti i termini della finanza spiegati in modo semplice e chiaro. 
            Cerca un termine o naviga per lettera.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#888780] w-5 h-5" />
            <Input
              type="text"
              placeholder="Cerca un termine..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedLetter(null);
              }}
              className="pl-10 bg-[#252523] border-white/[0.08] text-[#E8E8E4] placeholder:text-[#888780]"
              data-testid="glossary-search"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8 flex flex-wrap gap-2"
        >
          <Button
            onClick={() => { setSelectedLetter(null); setSearchQuery(''); }}
            className={selectedLetter === null && !searchQuery ? 'sapiens-btn-primary' : 'sapiens-btn-secondary'}
            size="sm"
            data-testid="letter-all"
          >
            Tutti
          </Button>
          {alphabet.map((letter) => (
            <Button
              key={letter}
              onClick={() => { setSelectedLetter(letter); setSearchQuery(''); }}
              className={`w-10 h-10 text-sm font-semibold ${selectedLetter === letter ? 'sapiens-btn-primary' : 'sapiens-btn-secondary'}`}
              data-testid={`letter-${letter}`}
            >
              {letter}
            </Button>
          ))}
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#3D7A52] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : terms.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-[#888780]">Nessun termine trovato</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-8"
          >
            {Object.entries(groupedTerms).sort(([a], [b]) => a.localeCompare(b)).map(([letter, letterTerms]) => (
              <div key={letter}>
                <h2 className="text-2xl font-medium text-[#C9A84C] mb-4 sticky top-20 bg-[#1C1C1A] py-2 z-10">
                  {letter}
                </h2>
                <div className="grid gap-4">
                  {letterTerms.map((term) => (
                    <motion.div
                      key={term.term_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="sapiens-card p-6 card-hover"
                      data-testid={`term-${term.term_id}`}
                    >
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-lg font-bold text-[#E8E8E4] leading-snug">{term.term}</h3>
                        {/* Deployment 22 — Sezione L: badge "Nuovo" se is_new=true e created_at < 30 giorni */}
                        {term.is_new === true && term.created_at && (() => {
                          const created = new Date(term.created_at);
                          const ageDays = (Date.now() - created.getTime()) / 86400000;
                          if (Number.isFinite(ageDays) && ageDays >= 0 && ageDays <= 30) {
                            return (
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                style={{ background: '#2e7d32', color: '#fff' }}
                                data-testid={`term-new-badge-${term.term_id}`}
                              >
                                Nuovo
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      <p className="text-[#888780] text-sm leading-relaxed">{term.definition}</p>
                      {term.related_lesson_id && (
                        <Link
                          to={`/lezione/${term.related_lesson_id}`}
                          className="inline-flex items-center gap-2 text-[#3D7A52] hover:text-[#C9A84C] text-sm transition-colors"
                        >
                          <BookOpen className="w-4 h-4" />
                          Vai alla lezione correlata
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default GlossarioPage;
